import type { SupportRequestFormData } from '../schemas/supportRequestFormSchema';
import type { SupportRequestDtoOut } from '@/models/out/SupportRequestDtoOut';
import { DocumentDtoOut } from '@/models/out/DocumentDtoOut';
import { FileReaderHelper } from '@/helpers/fileReaderHelper';
import { PaymentStartTiming } from '@/models/common/PaymentStartTiming';
import { KollelDetails } from '@/models/common/KollelDetails';
import { StudyTimeOptions } from '@/constants/studyTimeOptions';
import { AUTO_OFFICE_COMMENTS } from '@/constants/officeComments';
import { FundExpenseDetails } from '@/models/FundExpenseDetails';
import AllowedFor from '@/models/common/AllowedFor';
import { requiresDocumentDate } from '@/helpers/supportDocumentDate';

/** SPR-10015: the date the office checks a document against, by goal. Civil YYYY-MM-DD. */
type DocumentDateDetails = { weddingDate: string } | { documentDate: string };

/**
 * Transforms form data to API DTO structure
 */
export async function mapFormDataToDto(data: SupportRequestFormData): Promise<SupportRequestDtoOut> {
  // Determine requestedBecauseOfContactId based on supportRecipient
  // If supportRecipient is 'SupportedChildren', use childId
  // Otherwise (for 'Supported'), leave undefined so backend uses SupportedId
  const requestedBecauseOfContactId = data.supportRecipient === 'SupportedChildren' && data.childId
    ? data.childId
    : undefined;

  // Convert File objects to DocumentDtoOut
  // TODO: Extract to shared helper function for reuse across forms
  let documents: DocumentDtoOut[] = [];
  if ((data.documents || []).length > 0) {
    const convertedDocs = await Promise.all(
      (data.documents || []).map((doc: { file: File; requiredDocName: string }) =>
        FileReaderHelper.fileToDocumentDtoOut(doc.file, doc.requiredDocName)
      )
    );
    documents = convertedDocs.flat();
  }

  // Convert additional documents (user-added with custom descriptions)
  const additionalDocs = (data.additionalDocuments || []).filter(doc => doc.file);
  if (additionalDocs.length > 0) {
    const convertedAdditionalDocs = await Promise.all(
      additionalDocs.map((doc) =>
        FileReaderHelper.fileToDocumentDtoOut(doc.file!, doc.customDescription || doc.file!.name)
      )
    );
    documents = [...documents, ...convertedAdditionalDocs.flat()];
  }

  // Determine multiRequestTotalCount, paymentStartTiming, paymentStartMonth and transferDayOfMonth
  // For multiple months (>1), use selected values. Otherwise use defaults.
  const multiRequestTotalCount = data.multiRequestTotalCount ?? 1;
  const isMultipleMonths = multiRequestTotalCount > 1;

  const paymentStartTiming = isMultipleMonths
    ? data.paymentStartTiming
    : PaymentStartTiming.Immediate;

  const paymentStartMonth = isMultipleMonths && 
    data.paymentStartTiming === PaymentStartTiming.SpecificMonth && 
    data.paymentStartMonth
    ? parseInt(data.paymentStartMonth, 10)
    : undefined;

  const transferDayOfMonth = isMultipleMonths && data.transferDayOfMonth
    ? data.transferDayOfMonth
    : 0;


  // Combine all comments into an array
  const userComments: string[] = [];
  
  // Add wedding date to comments if it exists (for Wedding goal)
  // Note: Wedding date stays in userComments for backward compatibility
  if (data.weddingDate) {
    // Format date from YYYY-MM-DD to DD/MM/YYYY
    const [year, month, day] = data.weddingDate.split('-');
    const formattedDate = `${day}/${month}/${year}`;
    userComments.push(`תאריך חתונה: ${formattedDate}`);
  }
  // Build additionalDetails based on goal type
  let additionalDetails: KollelDetails | FundExpenseDetails | DocumentDateDetails | undefined;

  const isKollelGoal = data.goalWithLimits?.goal?.name === 'Kollel';
  const isFundExpenseGoal = data.goalWithLimits?.goal?.allowedFor === AllowedFor.Fund;
  
  if (isKollelGoal) {
    // Build additional details for Kollel (sent in dedicated field, not userComments)
    additionalDetails = {
      kollelName: data.kollelName?.trim(),
      studyTimes: (data.studyTimes || []) as StudyTimeOptions[],
      kollelAddress: data.kollelAddress?.trim(),
      studyMaterial: data.studyMaterial?.trim(),
      roshKollelName: data.roshKollelName?.trim(),
    } as KollelDetails;
  } else if (isFundExpenseGoal) {
    // Vendor identity (name/taxId) is persisted in the Suppliers table via the `supplier`
    // payload below. When the fund pays the vendor directly (isAlreadyPaid=false) the vendor's
    // bank details also live on the Suppliers row. When the fund reimburses a private payer
    // (isAlreadyPaid=true) the bank details on the form belong to the PAYER, not the vendor,
    // and are stored here in AdditionalDetails (never on the Suppliers row).
    additionalDetails = {
      isAlreadyPaid: data.isAlreadyPaid,
      ...(data.isAlreadyPaid && {
        payerName: data.payerName!,
        payerIsraeliId: data.payerIsraeliId!,
        payerBankCode: data.bankCode ?? null,
        payerBankBranch: data.bankBranch ?? null,
        payerBankAccount: data.bankAccount ?? null,
      }),
    } as FundExpenseDetails;
  } else if (data.goalWithLimits?.goal?.name === 'Wedding' && data.weddingDate) {
    // SPR-10015: structured too, so the office can check the date without parsing the comment.
    additionalDetails = { weddingDate: data.weddingDate };
  } else if (requiresDocumentDate(data.goalWithLimits?.goal) && data.documentDate) {
    additionalDetails = { documentDate: data.documentDate };
  }
  if (data.exceedsBalanceComment?.trim()) {
    userComments.push(data.exceedsBalanceComment.trim());
  }
  if (data.userComments?.trim()) {
    userComments.push(data.userComments.trim());
  }

  // Generate office comments based on exceeded limits
  const officeComments: string[] = [];
  if (data.exceedsAvailableBalance) {
    officeComments.push(AUTO_OFFICE_COMMENTS.NO_AVAILABLE_BALANCE);
  }
  if (data.exceedsEligibleAmount) {
    officeComments.push(AUTO_OFFICE_COMMENTS.EXCEEDS_ELIGIBILITY);
  }

  // For fund-expense requests, send a structured supplier payload so the backend can
  // link / find-or-create a Supplier row (by id when picked from the dropdown, or by
  // TaxId for a freshly-entered supplier). Bank details are NOT carried on the supplier
  // payload - they live on the dedicated SupplierPaymentInfos table (per fund + supplier)
  // and are sent via either `supplierPaymentInfoId` (existing stored account) or
  // `supplierNewBankAccount` (new account).
  const supplier = isFundExpenseGoal
    ? data.selectedSupplierId
      ? { id: data.selectedSupplierId }
      : {
          name: data.supplierName ?? undefined,
          taxId: data.supplierTaxId ?? undefined,
        }
    : undefined;

  // Decide which payment-routing fields to attach:
  //  - Beneficiary request: `paymentInfoId` carries the chosen PaymentInfo of the beneficiary.
  //  - Fund-expense + already-paid (reimbursement): no SupplierPaymentInfo is created; the
  //    payer's bank details are stored on AdditionalDetails (see above). No bank field is sent.
  //  - Fund-expense + paying directly + supplierPaymentInfoId > 0: user picked a stored account.
  //  - Fund-expense + paying directly + supplierPaymentInfoId === 0 (or undefined): user entered
  //    fresh bank details; send them as `supplierNewBankAccount` so the backend creates a new
  //    SupplierPaymentInfo row.
  let outgoingPaymentInfoId: number | undefined;
  let outgoingSupplierPaymentInfoId: number | undefined;
  let supplierNewBankAccount: {
    bankCode: number;
    bankBranch: string;
    bankAccount: string;
  } | undefined;

  if (isFundExpenseGoal) {
    if (!data.isAlreadyPaid) {
      if (data.supplierPaymentInfoId && data.supplierPaymentInfoId > 0) {
        outgoingSupplierPaymentInfoId = data.supplierPaymentInfoId;
      } else if (data.bankCode != null && data.bankBranch && data.bankAccount) {
        supplierNewBankAccount = {
          bankCode: data.bankCode,
          bankBranch: data.bankBranch,
          bankAccount: data.bankAccount,
        };
      }
    }
  } else {
    // Beneficiary support request - existing flow unchanged.
    outgoingPaymentInfoId = data.paymentInfoId || undefined;
  }

  return {
    supportedId: data.supported?.id ?? undefined,
    goalId: data.goalWithLimits!.goal.id,
    requestedBecauseOfContactId,
    documents,
    multiRequestTotalCount,
    paymentStartTiming,
    paymentStartMonth,
    transferDayOfMonth,
    paymentInfoId: outgoingPaymentInfoId,
    supplierPaymentInfoId: outgoingSupplierPaymentInfoId,
    requestedAmount: data.amount,
    userComments: userComments.length > 0 ? userComments : undefined,
    officeComments: officeComments.length > 0 ? officeComments : undefined,
    additionalDetails,
    supplier,
    supplierNewBankAccount,
    invoiceNumber: isFundExpenseGoal ? (data.invoiceNumber?.trim() || undefined) : undefined,
    // Only ever sent on the fund-expense path: the server rejects a projectId on any other
    // goal with a 400, so it must not leak in from stale form state.
    projectId: isFundExpenseGoal ? (data.projectId ?? undefined) : undefined,
    // HAT-1259. Never on the fund-expense path - that flow already pays a non-beneficiary through
    // the supplier, and the server rejects both at once. Everything else is sent only when the
    // flag is on, so stale form state from an un-ticked box cannot reach the server.
    ...buildThirdPartyFields(data, isFundExpenseGoal),
  };
}

/**
 * The third-party payee fields, or nothing at all. A saved payee is sent as an id and its details
 * are left out: the server reads them from the stored row, so a tampered form cannot redirect an
 * approved payee's money to another account.
 */
function buildThirdPartyFields(
  data: SupportRequestFormData,
  isFundExpenseGoal: boolean
): Partial<SupportRequestDtoOut> {
  if (!data.isThirdPartyPayment || isFundExpenseGoal) {
    return {};
  }

  if (data.thirdPartyPayeeId) {
    return {
      isThirdPartyPayment: true,
      thirdPartyPayeeId: data.thirdPartyPayeeId,
      thirdPartyReason: data.thirdPartyReason ?? undefined,
      thirdPartyReasonDetails: data.thirdPartyReason === 'Other'
        ? (data.thirdPartyReasonDetails?.trim() || undefined)
        : undefined,
    };
  }

  return {
    isThirdPartyPayment: true,
    thirdPartyReason: data.thirdPartyReason ?? undefined,
    thirdPartyReasonDetails: data.thirdPartyReason === 'Other'
      ? (data.thirdPartyReasonDetails?.trim() || undefined)
      : undefined,
    thirdPartyPayee: {
      name: data.thirdPartyPayeeName?.trim() ?? '',
      israeliId: data.thirdPartyPayeeIsraeliId?.trim() ?? '',
      relationship: data.thirdPartyRelationship ?? 'Other',
      bankCode: data.thirdPartyBankCode ?? 0,
      bankBranch: data.thirdPartyBankBranch?.trim() ?? '',
      bankAccount: data.thirdPartyBankAccount?.trim() ?? '',
    },
  };
}

